import { Glide, Shape, styled } from '@teko/ds-desktop';
import { HorizontalSlider } from '@teko/teko-ecommerce';
import { ProductCardProps } from '@teko/types/ecommerce';
import { LazyComponent } from 'components/LazyComponent';
import { useHamper, useTenantConfig } from 'contexts';
import { isServerless } from 'helpers';
import { useProductsWrapper } from 'modules/page-builder/hooks/usePbProducts';
import { SlotComponentProps } from 'modules/page-builder/types';
import { useEffect } from 'react';
import { HamperCard } from './HamperCard';

const DEFAULT_MAX_WIDTH = 1232;

enum ScreenSize {
  s = 1296,
  m = 1440,
  l = 1680,
}
export type HamperCarouselSlotProps = SlotComponentProps<
  {
    products: ProductCardProps[];
  },
  {
    borderProductCard?: boolean;
    regionName?: string;
    maxWidth?: number | string;
  }
>;

const HamperCarouselSlot: React.FC<HamperCarouselSlotProps> = ({
  collectionSlug,
  serverData,
  config = {},
}: HamperCarouselSlotProps) => {
  const { isMobile } = useTenantConfig();
  const { setProductHampers, productHampers } = useHamper();

  const { products } = useProductsWrapper(
    collectionSlug ?? '',
    serverData,
    true,
    50,
  );

  const productsPerRow = isMobile
    ? 2.3
    : Number(config?.maxWidth) <= DEFAULT_MAX_WIDTH || !isServerless()
    ? 5
    : window.innerWidth >= ScreenSize.l
    ? 6.5
    : window.innerWidth >= ScreenSize.m
    ? 5.5
    : window.innerWidth >= ScreenSize.s
    ? 5
    : 4.5;

  useEffect(() => {
    if (!!products && Array.isArray(products)) {
      const newHamperProducts = Array.from(
        new Map(
          [...productHampers, ...products].map((item) => [item.sku, item]),
        ).values(),
      );

      setProductHampers(newHamperProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  if (isMobile) {
    return (
      <div className="wrapper-horizontal-slider">
        <HorizontalSlider
          slidesPerView={productsPerRow || 2.3}
          className="horizontal-slider"
        >
          {products.map((product: ProductCardProps) => (
            <StyledCard key={product.sku} border={!!config?.borderProductCard}>
              <HamperCard {...product} />
            </StyledCard>
          ))}
        </HorizontalSlider>
      </div>
    );
  }

  return products?.length ? (
    <Wrapper>
      <Glide
        perView={productsPerRow || 5}
        perSwipe=""
        showControl={products.length > (productsPerRow || 5)}
        bound={true}
      >
        {products.map((item) => (
          <StyledCard key={item.sku} border={!!config?.borderProductCard}>
            <HamperCard {...item} />
          </StyledCard>
        ))}
      </Glide>
    </Wrapper>
  ) : null;
};

export const LazyHamperCarouselSlot: React.FC<HamperCarouselSlotProps> = (
  props,
) => {
  return (
    <LazyComponent>
      <HamperCarouselSlot {...props} />
    </LazyComponent>
  );
};

const Wrapper = styled(Shape)`
  .horizontal-slider,
  .glide__track {
    background-color: white;
    border-radius: 12px;
  }
  .glide__arrow--left {
    left: 1px !important;
    border-radius: 100px;
    background-color: #fff !important;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    &:after {
      height: 24px !important;
      width: 24px !important;
    }
  }
  .glide__arrow--right {
    right: 2px !important;
    border-radius: 100px;
    background-color: #fff !important;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
    &:after {
      height: 24px !important;
      width: 24px !important;
    }
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 0.5rem;
`;

const StyledCard = styled(Card)<{ border: boolean }>(({ theme, border }) => ({
  height: '100%',
  ...(border
    ? {
        border: '1px solid',
        borderColor: theme.colors.divider,
        borderRadius: theme.spacing.s8,
        overflow: 'hidden',
      }
    : {}),
}));
